import { formatDay } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface singleResearchPaperPops {
    personImg: any;
    research: any;
    isActive?: boolean;
    onClick?: () => void;
}

export default function SingleResearchPaper({ 
    personImg, 
    research, 
    isActive, 
    onClick 
}: singleResearchPaperPops) {
    const { t } = useTranslation();
    
    // Get team information
    const teamMembers = research.prosecutor_assignments || [];
    const teamLeader = teamMembers.find((p: any) => p.is_team_leader);
    const totalMembers = teamMembers.length;
    
    // Check if there are exactly two pagenesh items
    const hasTwoPagenesh = research.pagenesh_count === 2 || 
                           (Array.isArray(research.pagenesh) && research.pagenesh.length === 2) ||
                           research.has_two_pagenesh === true;
    
    return (
        <div 
            onClick={onClick} 
            className={`flex flex-col rounded-lg shadow-md p-6 mb-4 cursor-pointer transition-all duration-200
                ${isActive ? "bg-gray-50 border-l-4 border-gray-400" : "bg-white hover:bg-gray-50"}`}
        >
            <div className="flex gap-1 font-regular text-xs text-[#747171] mb-3">
                <p>{formatDay(research.updated_at).weekday},</p>
                <p>{formatDay(research.updated_at).rest}</p>
            </div>
            
            <div>
                <div className="flex items-center mb-3 text-base font-semibold text-[#212121] gap-2">
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                        className="flex-shrink-0"
                    >
                        <path 
                            d="M24 9.35869C24 10.4661 23.032 11.413 21.9 11.413H17.2195C17.9854 12.2122 18.0795 13.7458 16.9242 14.7385C17.594 15.8086 17.0249 17.2237 16.1531 17.6595C16.5738 19.963 15.1244 21 12.75 21C12.6213 21 12.1277 20.9905 12 20.9909C9.09511 20.9987 8.39559 19.5347 6.20011 19.1948C5.65294 19.1101 5.25 18.6375 5.25 18.0837V10.0435L5.25009 10.0434C5.25061 9.18253 5.7473 8.36114 6.58434 7.9882C7.93837 7.37911 11.0568 5.68955 11.625 4.36369C11.9905 3.51084 12.6274 3.00038 13.5 3C15.1042 2.99934 16.2071 4.64522 15.5681 6.13631C15.4001 6.52814 15.1785 6.91772 14.9047 7.30434H21.9C22.9992 7.30434 24 8.2673 24 9.35869ZM4.5 9.375V18.375C4.5 18.9963 3.99633 19.5 3.375 19.5H1.125C0.503672 19.5 0 18.9963 0 18.375V9.375C0 8.75367 0.503672 8.25 1.125 8.25H3.375C3.99633 8.25 4.5 8.75367 4.5 9.375ZM3.1875 17.25C3.1875 16.7322 2.76778 16.3125 2.25 16.3125C1.73222 16.3125 1.3125 16.7322 1.3125 17.25C1.3125 17.7678 1.73222 18.1875 2.25 18.1875C2.76778 18.1875 3.1875 17.7678 3.1875 17.25Z" 
                            fill={isActive ? "#6b7280" : "#212121"}
                        />
                    </svg>
                    <p className="font-bold text-lg">
                        {t('ResearchDataPublishedPublic')}
                        <span className="text-gray-600 ml-1">{t('RegNumber')} {research.registry_reference_number}</span>
                    </p>
                </div>
                
                {/* Implementation Direction with better paragraph formatting */}
                <div className="mb-4">
                    {research.implementation_direction ? (
                        <div className="space-y-2 max-h-20 overflow-hidden">
                            {research.implementation_direction.split('\n').map((paragraph: string, index: number) => (
                                paragraph.trim() !== '' && (
                                    <p 
                                        key={index} 
                                        className="font-light text-sm text-[#212121] leading-relaxed line-clamp-2"
                                    >
                                        {paragraph}
                                    </p>
                                )
                            ))}
                        </div>
                    ) : (
                        <p className="font-light text-sm text-gray-500 italic">
                            {t('NoImplementationDirectionAvailable')}
                        </p>
                    )}
                </div>

                <div className="flex gap-4 mb-4">
                    <svg 
                        className="w-8 h-fit" 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="32" 
                        height="32" 
                        viewBox="0 0 20 20" 
                        fill="none"
                    >
                        <path 
                            d="M11.5326 20H8.00258C7.75258 20 7.54258 19.82 7.50258 19.58L7.13258 16.93C6.50258 16.68 5.96258 16.34 5.44258 15.94L2.95258 16.95C2.73258 17.03 2.46258 16.95 2.34258 16.73L0.342578 13.27C0.281374 13.167 0.259797 13.0452 0.281883 12.9274C0.303969 12.8096 0.368205 12.7039 0.462578 12.63L2.57258 10.97C2.53258 10.65 2.50258 10.33 2.50258 10C2.50258 9.67 2.53258 9.34 2.57258 9L0.462578 7.37C0.368205 7.29613 0.303969 7.19042 0.281883 7.07263C0.259797 6.95484 0.281374 6.83304 0.342578 6.73L2.34258 3.27C2.46258 3.05 2.73258 2.96 2.95258 3.05L5.44258 4.05C5.96258 3.66 6.50258 3.32 7.13258 3.07L7.50258 0.42C7.54258 0.18 7.75258 0 8.00258 0H12.0026C12.2526 0 12.4626 0.18 12.5026 0.42L12.8726 3.07C13.5026 3.32 14.0426 3.66 14.5626 4.05L17.0526 3.05C17.2726 2.96 17.5426 3.05 17.6626 3.27L19.6626 6.73C19.7826 6.95 19.7326 7.22 19.5426 7.37L17.4326 9C17.4726 9.34 17.5026 9.67 17.5026 10V10.19C17.0026 10.07 16.5026 10 16.0026 10C15.0826 10 14.2226 10.21 13.4426 10.58C13.4726 10.39 13.5026 10.2 13.5026 10C13.5026 8.07 11.9326 6.5 10.0026 6.5C8.07258 6.5 6.50258 8.07 6.50258 10C6.50258 11.93 8.07258 13.5 10.0026 13.5C10.2026 13.5 10.3926 13.47 10.5826 13.44C10.0751 14.505 9.89444 15.6964 10.0635 16.864C10.2326 18.0315 10.7438 19.1228 11.5326 20ZM14.0026 13V19L19.0026 16L14.0026 13Z" 
                            fill={isActive ? "#d1d5db" : "#D4D4D4"}
                        />
                    </svg>
                </div>
                
                {/* Team Members Display */}
                <div className="flex items-center mb-2">
                    <div className="flex items-center flex-1">
                        {/* Team Leader Avatar */}
                        <div className="relative mr-3">
                            <img
                                src={personImg}
                                alt={t('TeamLeaderAvatarAlt')}
                                width={40}
                                height={40}
                                className="object-cover rounded-full h-fit"
                            />
                            {totalMembers > 1 && (
                                <div className="absolute -bottom-1 -right-1 bg-gray-400 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    +{totalMembers - 1}
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <p className="text-[#0A1D39] font-semibold text-base">
                                {teamLeader?.prosecutor_name || t('Unknown')}
                                {totalMembers > 1 && (
                                    <span className="text-gray-500 text-sm font-normal ml-2">
                                        {t('PlusMoreMembers', { count: totalMembers - 1 })}
                                    </span>
                                )}
                            </p>
                            <div className="flex items-center gap-2">
                                <p className="text-[#9D0303] font-medium text-xs">{t('Researcher')}</p>
                                {totalMembers > 0 && (
                                    <div className="flex items-center text-gray-500 text-xs">
                                        <svg 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="12" 
                                            height="12" 
                                            viewBox="0 0 24 24" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            strokeWidth="2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                            className="mr-1"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        {t('MemberCount', { count: totalMembers })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex flex-col items-end">
                        <button className={`rounded-lg px-4 py-2 h-fit font-semibold text-sm shadow-md transition-all duration-200 ${
                            isActive
                                ? "bg-green-500 text-white hover:bg-green-600"
                                : "bg-green-600 text-white hover:bg-green-700"
                        }`}>
                            {t('Published')}
                        </button>
                        
                        {/* Conditional Pagenesh display - only shows if there are two pagenesh items */}
                        {hasTwoPagenesh && (
                            <div className="mt-2 flex items-center text-xs">
                                <div className={`flex items-center px-2 py-1 rounded ${
                                    isActive 
                                        ? "bg-gray-300 text-gray-700" 
                                        : "bg-blue-100 text-blue-800"
                                }`}>
                                    <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="12" 
                                        height="12" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                        className="mr-1"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4"></path>
                                        <path d="M12 8h.01"></path>
                                    </svg>
                                    <span>{t('PageneshTwoItems')}</span>
                                </div>
                            </div>
                        )}
                        
                        {/* Optional: Show different message when not two pagenesh items */}
                        {research.pagenesh && !hasTwoPagenesh && (
                            <div className="mt-2 flex items-center text-xs text-gray-500">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="mr-1"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{t('PageneshCount', { count: typeof research.pagenesh === 'string' ? research.pagenesh : 'Available' })}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}